import { t } from 'i18next';

/**
 * `t(key)`, guaranteed to return something renderable.
 *
 * `i18next`'s standalone `t` answers `undefined` when the instance has not been initialised — during
 * early module evaluation, and in unit tests, which do not boot i18n. Callers here feed the result
 * into `?? someGenericFallback` or straight into an error banner, so an `undefined` silently becomes
 * either the wrong message or a blank one.
 *
 * Both failure modes were live in the onboarding flow (SEE-1274): a specific refusal reason was
 * replaced by "we could not start your application", and a rejected save set `session.error` to
 * `undefined` so the error surface rendered nothing at all.
 *
 * Falling back to the key is deliberate. A visible `couldNotSaveApplication` on screen is ugly and
 * gets reported; a blank banner or a generic message hides the fact that the copy never resolved.
 *
 * For components, prefer `useTranslation()` — this exists for plain modules (thunks, reducers,
 * message maps) that have no hook context.
 */
export const translateOrKey = (key: string): string => {
  const translated = t(key);
  return typeof translated === 'string' && translated ? translated : key;
};

export default translateOrKey;
